import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const B = {
  gold: "#D4A843", goldFaint: "#D4A84318",
  blue: "#4A90D9", blueFaint: "#4A90D918",
  green: "#43B581", red: "#E74C3C",
  bg: "#0D0F13", card: "#13161C",
  border: "#1E222A", white: "#F0F0F0",
  whiteGray: "#A0A8B8", gray: "#6B7280",
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useBookmarks(user) {
  const [bookmarks, setBookmarks] = useState([]);
  const [bookmarkIds, setBookmarkIds] = useState(new Set());

  const fetchBookmarks = useCallback(async () => {
    if (!user) { setBookmarks([]); setBookmarkIds(new Set()); return; }
    try {
      const { data } = await axios.get(`${API}/bookmarks`, { withCredentials: true });
      setBookmarks(data);
      setBookmarkIds(new Set(data.map(b => b.lesson_id)));
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => { fetchBookmarks(); }, [fetchBookmarks]);

  const toggleBookmark = async (lesson) => {
    if (!user) return;
    const isBookmarked = bookmarkIds.has(lesson.id);
    try {
      if (isBookmarked) {
        await axios.delete(`${API}/bookmarks/${lesson.id}`, { withCredentials: true });
        setBookmarkIds(prev => { const s = new Set(prev); s.delete(lesson.id); return s; });
        setBookmarks(prev => prev.filter(b => b.lesson_id !== lesson.id));
      } else {
        await axios.post(`${API}/bookmarks`, {
          lesson_id: lesson.id, course: lesson.course, title: lesson.title,
          tag: lesson.tag || "", track: lesson.track || "",
          phase_label: lesson.phaseLabel || "", duration: lesson.duration || "",
        }, { withCredentials: true });
        setBookmarkIds(prev => new Set(prev).add(lesson.id));
        setBookmarks(prev => [{ lesson_id: lesson.id, course: lesson.course, title: lesson.title, tag: lesson.tag, track: lesson.track, phase_label: lesson.phaseLabel, duration: lesson.duration }, ...prev]);
      }
    } catch { /* ignore */ }
  };

  return { bookmarks, bookmarkIds, toggleBookmark, fetchBookmarks };
}

export default function BookmarksPanel({ bookmarks, onRemove }) {
  const navigate = useNavigate();
  const courseAccent = (course) => course === "beginner" ? B.blue : B.gold;

  if (!bookmarks || bookmarks.length === 0) return null;

  return (
    <div data-testid="bookmarks-panel" style={{ width: "100%", maxWidth: 1100, margin: "0 auto", padding: "0 24px 32px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill={B.gold} stroke={B.gold} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
        </svg>
        <span style={{ fontSize: 13, fontWeight: 600, color: B.gold, letterSpacing: 1, fontFamily: "monospace" }}>YOUR BOOKMARKS</span>
        <span style={{ fontSize: 11, color: B.gray }}>({bookmarks.length})</span>
      </div>
      <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 8 }}>
        {bookmarks.map((b) => (
          <div key={b.lesson_id} data-testid={`bookmark-${b.lesson_id}`}
            style={{
              flexShrink: 0, width: 260, background: B.card, border: `1px solid ${B.border}`,
              borderRadius: 10, padding: "14px 16px", cursor: "pointer",
              transition: "border-color 0.2s, transform 0.2s",
            }}
            onClick={() => navigate(`/${b.course}`)}
            onMouseEnter={e => { e.currentTarget.style.borderColor = courseAccent(b.course) + "50"; e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = B.border; e.currentTarget.style.transform = "translateY(0)"; }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
              <span style={{
                fontSize: 9, fontWeight: 700, padding: "2px 5px", borderRadius: 3,
                background: courseAccent(b.course) + "20", color: courseAccent(b.course),
              }}>
                {b.course === "beginner" ? "BEGINNER" : "ADVANCED"}
              </span>
              <button
                data-testid={`remove-bookmark-${b.lesson_id}`}
                onClick={(e) => { e.stopPropagation(); onRemove(b); }}
                style={{ background: "transparent", border: "none", cursor: "pointer", color: B.gray, fontSize: 12, padding: 2, lineHeight: 1 }}
                title="Remove bookmark"
              >
                &#x2715;
              </button>
            </div>
            <div style={{ fontSize: 12, color: B.white, fontWeight: 500, lineHeight: 1.4, marginBottom: 6 }}>{b.title}</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {b.tag && <span style={{ fontSize: 9, color: B.gold }}>{b.tag}</span>}
              {b.track && <span style={{ fontSize: 9, color: B.gray }}>{b.track}</span>}
              {b.duration && <span style={{ fontSize: 9, color: B.gray }}>{b.duration}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
